import { cn } from '@/platform/lib/utils';
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Description,
    Field,
    Label,
} from '@headlessui/react';
import {
    CheckIcon,
    ChevronUpDownIcon,
    XMarkIcon,
} from '@heroicons/react/16/solid';
import { useId, useState } from 'react';

// Option ids and selected values can differ in primitive type (e.g. options
// built from Object.keys() are strings while the server sends numeric ids), so
// every comparison in this component goes through one rule.
const sameId = (a, b) => String(a) === String(b);

const Dropdown = ({
    id,
    label,
    description,
    error,
    required = false,
    disabled = false,
    multiple = false,
    placeholder,
    options = [],
    value,
    className,
    ...props
}) => {
    let uuid = useId();
    const [query, setQuery] = useState('');

    const selectedValues = multiple ? (value ?? []) : value;

    const optionName = val =>
        options.find(option => sameId(option.id, val))?.name ?? '';

    const filteredOptions =
        query === ''
            ? options
            : options.filter(option =>
                  option?.name?.toLowerCase().includes(query.toLowerCase())
              );

    const removeValue = val => {
        props.onChange(
            selectedValues.filter(selected => !sameId(selected, val))
        );
    };

    return (
        <Field disabled={disabled} className={className}>
            {label && (
                <Label
                    htmlFor={id || uuid}
                    className='mb-1 block text-sm/6 font-medium text-gray-900'
                >
                    {label}
                    {required && (
                        <span className='inline-block pl-1 text-red-800'>
                            *
                        </span>
                    )}
                </Label>
            )}
            {description && (
                <Description className='mb-1 text-sm/6 text-gray-500'>
                    {description}
                </Description>
            )}
            <Combobox
                {...props}
                immediate
                by={sameId}
                multiple={multiple}
                value={selectedValues}
                onClose={() => setQuery('')}
                invalid={!!error}
            >
                <div className='relative'>
                    <div
                        className={cn(
                            'focus-within:outline-primary-600 flex w-full flex-wrap items-center gap-1 rounded-md bg-white py-1.5 pr-8 pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 has-disabled:cursor-not-allowed has-disabled:bg-gray-50',
                            error &&
                                'outline-red-300 focus-within:outline-red-600'
                        )}
                    >
                        {multiple &&
                            selectedValues.map(val => (
                                <span
                                    key={val}
                                    className={cn(
                                        'inline-flex items-center gap-1 rounded bg-gray-100 py-0.5 pr-1 pl-2 text-sm text-gray-700',
                                        error && 'bg-red-50 text-red-900'
                                    )}
                                >
                                    {optionName(val)}
                                    <button
                                        type='button'
                                        onClick={() => removeValue(val)}
                                        className={cn(
                                            'rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600',
                                            error &&
                                                'text-red-400 hover:bg-red-100 hover:text-red-600'
                                        )}
                                        aria-label={`Verwijder ${optionName(val)}`}
                                    >
                                        <XMarkIcon className='size-3.5' />
                                    </button>
                                </span>
                            ))}
                        <ComboboxInput
                            id={id || uuid}
                            required={required}
                            placeholder={placeholder}
                            displayValue={val =>
                                multiple ? '' : optionName(val)
                            }
                            onChange={event => setQuery(event.target.value)}
                            className={cn(
                                'min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-gray-900 ring-0 placeholder:text-gray-400 focus:ring-0 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-500 sm:text-sm/6',
                                error && 'text-red-900 placeholder:text-red-300'
                            )}
                        />
                    </div>
                    <ComboboxButton className='absolute inset-y-0 right-0 flex items-center pr-2'>
                        <ChevronUpDownIcon
                            aria-hidden='true'
                            className='size-5 text-gray-500 sm:size-4'
                        />
                    </ComboboxButton>
                    <ComboboxOptions
                        modal={false}
                        className='absolute z-51 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 empty:invisible sm:text-sm/6'
                    >
                        {filteredOptions.map(option => (
                            <ComboboxOption
                                key={option.id}
                                value={option.id}
                                className='group data-focus:bg-primary-600 flex cursor-default items-center justify-between px-3 py-1.5 text-gray-900 transition-colors select-none data-focus:text-white'
                            >
                                <span className='block truncate group-data-selected:font-semibold'>
                                    {option.name}
                                </span>
                                <CheckIcon
                                    aria-hidden='true'
                                    className='text-primary-600 invisible size-4 group-data-focus:text-white group-data-selected:visible'
                                />
                            </ComboboxOption>
                        ))}
                    </ComboboxOptions>
                </div>
                {error && (
                    <Description className='mt-1 text-sm text-red-600'>
                        {error}
                    </Description>
                )}
            </Combobox>
        </Field>
    );
};

export default Dropdown;
